import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Body, Grid, Select } from '@platform/ui';
import { LinePlot } from '../plots/LinePlot';
import { WaterfallPlot } from '../plots/WaterfallPlot';
import module from '../../../modules/dicom-measurement/src';

const {
  guideline,
  getTimepoints,
  getTimepointStats,
  getAIAssessments,
} = module;
const { getMeasurementTechniques, getBodyParts, getTrackingTypes } = guideline;

export const AnalysisSection = ({
  imageSets,
  ROIContours,
  criteria,
  assessments,
  plotType,
  getFilteredROIContours,
  filteredBodyPart,
  setFilteredBodyPart,
  filteredTrackingType,
  setFilteredTrackingType,
  selectedLesionNumber,
  setSelectedLesionNumber,
  measurementTechnique,
  setMeasurementTechnique,
}) => {
  const [xAxisKey] = useState('name');
  const [yAxisKey, setYAxisKey] = useState('comparedSumDiameter');
  const [ySumAxisKey, setYSumAxisKey] = useState('sumDiameter');

  const timepoints = getTimepoints(imageSets, ROIContours);
  const ai_assessments = getAIAssessments(
    timepoints,
    ROIContours,
    criteria,
    assessments
  );

  const filteredROIContours = getFilteredROIContours(ROIContours);
  const selectedROIContour = ROIContours.find(
    roi => roi.ROINumber === selectedLesionNumber
  );
  const selectedStats = getTimepointStats(
    timepoints,
    filteredROIContours,
    selectedROIContour ? [selectedROIContour] : [],
    criteria,
    assessments
  );
  const measurements = selectedStats.map(series => ({
    ...series.summation,
    date: series.date,
  }));
  const changesFromBaseline = selectedStats.map((series, index) => ({
    ...series.fromBaseline,
    date: series.date,
    result: ai_assessments[index]?.overallResponse?.result,
  }));

  const onBodyPartChange = evt => {
    setFilteredBodyPart(evt.target.value);
  };
  const onTrackingTypeChange = evt => {
    const type = evt.target.value;
    setFilteredTrackingType(type);
    if (type !== 'selected') {
      setSelectedLesionNumber(-1);
    }
  };
  const onLesionNumberChange = evt => {
    setSelectedLesionNumber(Number(evt.target.value));
  };
  const onTechniqueChange = evt => {
    setMeasurementTechnique(evt.target.value);
  };

  useEffect(() => {
    switch (measurementTechnique) {
      case 'BIDIMENSIONAL':
        setYAxisKey('comparedSumDiameter');
        setYSumAxisKey('sumDiameter');
        break;
      case 'VOLUMETRIC':
        setYAxisKey('comparedSumVolume');
        setYSumAxisKey('sumVolume');
        break;
    }
  }, [measurementTechnique]);

  return (
    <Body>
      <TabPanel value={plotType} index={'measurement'}>
        <LinePlot
          data={measurements}
          xAxisKey={xAxisKey}
          yAxisKey={yAxisKey}
          ySumAxisKey={ySumAxisKey}
        />
      </TabPanel>
      <TabPanel value={plotType} index={'changeFromBaseline'}>
        <WaterfallPlot
          data={changesFromBaseline}
          xAxisKey={xAxisKey}
          yAxisKey={yAxisKey}
          ySumAxisKey={ySumAxisKey}
        />
      </TabPanel>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <Select value={filteredBodyPart} onChange={onBodyPartChange}>
            {getBodyParts().map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Grid>
        <Grid item xs={3}>
          <Select value={filteredTrackingType} onChange={onTrackingTypeChange}>
            {getTrackingTypes(criteria.guidelineId).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Grid>
        <Grid item xs={3}>
          <Select
            value={selectedLesionNumber}
            onChange={onLesionNumberChange}
            size="small"
            variant="outlined"
            fullWidth
          >
            <option key={-1} value={-1}>
              ------
            </option>
            {(filteredTrackingType === 'selected'
              ? ROIContours
              : filteredROIContours
            ).map(roi => (
              <option key={roi.ROINumber} value={roi.ROINumber}>
                {roi.ROIName}
              </option>
            ))}
          </Select>
        </Grid>
        <Grid item xs={3}>
          <Select value={measurementTechnique} onChange={onTechniqueChange}>
            {getMeasurementTechniques().map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Grid>
      </Grid>
    </Body>
  );
};
AnalysisSection.propTypes = {
  imageSets: PropTypes.array,
  ROIContours: PropTypes.array,
  criteria: PropTypes.any,
  assessments: PropTypes.array,
  plotType: PropTypes.string,
  getFilteredROIContours: PropTypes.func,
  filteredBodyPart: PropTypes.string,
  setFilteredBodyPart: PropTypes.func,
  filteredTrackingType: PropTypes.string,
  setFilteredTrackingType: PropTypes.func,
  selectedLesionNumber: PropTypes.number,
  setSelectedLesionNumber: PropTypes.func,
  measurementTechnique: PropTypes.string,
  setMeasurementTechnique: PropTypes.func,
};

const TabPanel = props => {
  const { children, value, index } = props;
  return value === index && children;
};
